/** QuickRouter — Composant racine : accueil projets, puis atelier (bibliothèque / nets,
 *  schéma, carte 2D ou 3D, propriétés / assistant) ou fabrication. Raccourcis clavier globaux
 *  (ceux du schéma sont gérés par son éditeur). */
import { routableLayers } from '../shared/layers.js';
import { useEffect } from 'react';
import { useProjectStore } from './store/useProjectStore.js';
import ProjectPicker from './components/ProjectPicker.jsx';
import LibraryPanel from './components/LibraryPanel.jsx';
import NetsPanel from './components/NetsPanel.jsx';
import PropsPanel from './components/PropsPanel.jsx';
import ChatPanel from './components/ChatPanel.jsx';
import FabView from './components/FabView.jsx';
import StatusBar from './components/StatusBar.jsx';
import Toolbar from './components/Toolbar.jsx';
import Board2D from './board/Board2D.jsx';
import Board3D from './board/Board3D.jsx';
import SchematicView from './schematic/SchematicView.jsx';
import SchToolbar from './schematic/SchToolbar.jsx';

function useShortcuts() {
  useEffect(() => {
    const onKey = (e) => {
      const st = useProjectStore.getState();
      if (!st.project || st.mode === 'fab' || st.dialog) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === 'z') { e.preventDefault(); e.shiftKey ? st.redo() : st.undo(); return; }
      if ((e.ctrlKey || e.metaKey) && k === 'y') { e.preventDefault(); st.redo(); return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (st.mode !== 'board') return;
      if (st.tool === 'track' && ['v', 'enter', 'backspace', 'escape'].includes(k)) return; // gérés par l'éditeur
      if (e.key === 'Delete' || e.key === 'Backspace') st.deleteSelection();
      else if (e.key === 'Escape') { if (st.tool !== 'select') st.setTool('select'); else { st.setSelection([]); st.setHighlightNet(null); } }
      else if (k === 'r') st.rotateSelection(e.shiftKey ? -90 : 90);
      else if (k === 'f') { if (st.selection.length) st.flipSelection(); else st.fit(); }
      else if (k === 'x') st.setTool('track');
      else if (k === 'v') { const sig = routableLayers(st.project.board); st.setLayer(sig[(sig.indexOf(st.layer) + 1) % sig.length] ?? 'F'); }
      else if (k === 'z') st.fit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export default function App() {
  const project = useProjectStore((s) => s.project);
  const mode = useProjectStore((s) => s.mode);
  const setMode = useProjectStore((s) => s.setMode);
  const leftTab = useProjectStore((s) => s.leftTab);
  const setLeftTab = useProjectStore((s) => s.setLeftTab);
  const rightTab = useProjectStore((s) => s.rightTab);
  const setRightTab = useProjectStore((s) => s.setRightTab);
  const closeProject = useProjectStore((s) => s.closeProject);
  const saveState = useProjectStore((s) => s.saveState);
  const undo = useProjectStore((s) => s.undo);
  const redo = useProjectStore((s) => s.redo);
  const canUndo = useProjectStore((s) => s.past.length > 0);
  const canRedo = useProjectStore((s) => s.future.length > 0);
  const aiJob = useProjectStore((s) => s.ai.job);
  useShortcuts();

  if (!project) return <ProjectPicker />;

  const saveLabel = { saving: '💾 Sauvegarde…', saved: '✓ Sauvegardé', error: '⚠ Erreur de sauvegarde', paused: '⏸ Assistant en cours' }[saveState] ?? '';

  return (
    <div className="app">
      <div className="topbar">
        <h1>Quick<span className="logo-sfx">·Router</span></h1>
        <button onClick={closeProject} title="Retour aux projets">←</button>
        <span className="project-name">{project.name}</span>
        {project.role === 'viewer'
          ? <span className="ro-badge" title="Projet partagé en lecture seule">👁 Lecture seule</span>
          : <span className={`save-state ${saveState === 'error' ? 'error' : ''}`}>{saveLabel}</span>}
        <div className="seg">
          <button disabled={!canUndo || !!aiJob} onClick={undo} title="Annuler (Ctrl+Z)">↶</button>
          <button disabled={!canRedo || !!aiJob} onClick={redo} title="Refaire (Ctrl+Y)">↷</button>
        </div>
        <span className="spacer" />
        {aiJob && <span className="ai-badge"><span className="spinner" /> L'assistant conçoit la carte</span>}
        <div className="tabs">
          <button className={mode === 'sch' ? 'active' : ''} onClick={() => setMode('sch')} title="L'étude : schéma électrique, d'où découle la netlist">Schéma</button>
          <button className={mode === 'board' ? 'active' : ''} onClick={() => setMode('board')}>Carte</button>
          <button className={mode === '3d' ? 'active' : ''} onClick={() => setMode('3d')}>3D</button>
          <button className={mode === 'fab' ? 'active' : ''} onClick={() => setMode('fab')}>Fabrication</button>
        </div>
      </div>

      {mode === 'fab' ? (
        <div className="main scroll"><FabView /></div>
      ) : (
        <div className="main">
          <aside className="side left">
            <div className="side-tabs">
              <button className={leftTab === 'library' ? 'active' : ''} onClick={() => setLeftTab('library')}>Empreintes</button>
              <button className={leftTab === 'nets' ? 'active' : ''} onClick={() => setLeftTab('nets')}>Nets</button>
            </div>
            {leftTab === 'library' ? <LibraryPanel /> : <NetsPanel />}
          </aside>
          <div className="canvas-area">
            {mode === '3d' ? <Board3D />
              : mode === 'sch' ? (<><SchToolbar /><div className="board-host"><SchematicView /></div></>)
                : (<><Toolbar /><div className="board-host"><Board2D /></div></>)}
          </div>
          <aside className="side right">
            <div className="side-tabs">
              <button className={rightTab === 'props' ? 'active' : ''} onClick={() => setRightTab('props')}>Propriétés</button>
              <button className={rightTab === 'chat' ? 'active' : ''} onClick={() => setRightTab('chat')}>Assistant{aiJob ? ' ●' : ''}</button>
            </div>
            {rightTab === 'props' ? <div className="panel-body"><PropsPanel /></div> : <ChatPanel />}
          </aside>
        </div>
      )}
      <StatusBar />
    </div>
  );
}
